import { Button } from "@softmax/design-system";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { RevisionRejectionForm } from "./revision-rejection-form";
import type {
  RevisionReviewActions,
  StoreEditorRevision,
} from "./revision-records";

type StoreEditorRevisionRowProps = Readonly<{
  revision: StoreEditorRevision;
  canChangeRevision: boolean;
  reviewActions: RevisionReviewActions | undefined;
  rejectionReason: string | null;
  onBeginRejection(id: string): void;
  onReasonChange(reason: string): void;
  onRevert(id: string): void;
}>;

const statusLabels = {
  draft: "Draft",
  pending_review: "Pending review",
  published: "Published",
  rejected: "Rejected",
};

export function StoreEditorRevisionRow({
  revision,
  canChangeRevision,
  reviewActions,
  rejectionReason,
  onBeginRejection,
  onReasonChange,
  onRevert,
}: StoreEditorRevisionRowProps) {
  return (
    <li {...stylex.props(styles.revision)}>
      <div {...stylex.props(styles.summary)}>
        <span>v{revision.version}</span>
        <span {...stylex.props(styles.muted)}>
          {statusLabels[revision.status]}
        </span>
        <time dateTime={revision.createdAt} {...stylex.props(styles.muted)}>
          {new Date(revision.createdAt).toLocaleString()}
        </time>
        {revision.note && (
          <span {...stylex.props(styles.muted)}>— {revision.note}</span>
        )}
      </div>
      <div {...stylex.props(styles.actions)}>
        {reviewActions && (
          <>
            <Button
              size="sm"
              disabled={!canChangeRevision}
              onClick={() => reviewActions.approve(revision.id)}
            >
              Approve
            </Button>
            <Button
              size="sm"
              disabled={!canChangeRevision}
              onClick={() => onBeginRejection(revision.id)}
            >
              Reject
            </Button>
          </>
        )}
        <Button
          size="sm"
          disabled={!canChangeRevision}
          onClick={() => onRevert(revision.id)}
        >
          Revert to this
        </Button>
      </div>
      {reviewActions && rejectionReason !== null && (
        <RevisionRejectionForm
          version={revision.version}
          reason={rejectionReason}
          canChangeRevision={canChangeRevision}
          onReasonChange={onReasonChange}
          onReject={() =>
            reviewActions.reject({ id: revision.id, reason: rejectionReason })
          }
        />
      )}
    </li>
  );
}

const styles = stylex.create({
  revision: {
    display: "grid",
    gap: spacing.space8,
    paddingBlock: spacing.space8,
    borderBlockStartWidth: 1,
    borderBlockStartStyle: "solid",
    borderBlockStartColor: colors.border,
  },
  summary: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    gap: spacing.space8,
    fontFamily: fonts.mono,
    fontSize: fontSizes.caption,
  },
  muted: { color: colors.muted },
  actions: { display: "flex", flexWrap: "wrap", gap: spacing.space6 },
});
